/**
 * ChatPage Component
 * x402 Conversational AI search interface for professional license data.
 * Users can ask natural language questions that get translated into DB queries.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { Send, Plus, Trash2, MessageSquare, Database, Loader2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

// --- Types ---

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  query?: string | null;
  results?: Record<string, unknown>[] | null;
  resultsCount?: number;
  created_at?: string;
}

interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

// --- Sub-components ---

const ResultsTable: React.FC<{ results: Record<string, unknown>[] }> = ({ results }) => {
  if (!results || results.length === 0) return null;

  // Get column headers from first result
  const columns = Object.keys(results[0]).filter(
    (col) => !['raw_data', 'id'].includes(col)
  );

  // Limit visible columns to keep it readable
  const visibleColumns = columns.slice(0, 8);

  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-slate-700">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-slate-800/80">
            {visibleColumns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left text-slate-300 font-medium whitespace-nowrap"
              >
                {col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {results.map((row, i) => (
            <tr key={i} className="hover:bg-slate-800/40 transition-colors">
              {visibleColumns.map((col) => (
                <td key={col} className="px-3 py-2 text-slate-400 whitespace-nowrap max-w-[200px] truncate">
                  {String(row[col] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {columns.length > visibleColumns.length && (
        <div className="px-3 py-1.5 text-xs text-slate-500 bg-slate-800/40 border-t border-slate-700">
          + {columns.length - visibleColumns.length} more columns hidden
        </div>
      )}
    </div>
  );
};

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[85%] ${
          isUser
            ? 'bg-sky-600/80 text-white rounded-2xl rounded-br-md'
            : 'bg-slate-800/80 text-slate-200 rounded-2xl rounded-bl-md border border-slate-700/50'
        } px-4 py-3`}
      >
        {/* Message content */}
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>

        {/* SQL query display */}
        {message.query && (
          <div className="mt-3 bg-slate-900/60 rounded-lg p-3 border border-slate-600/30">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Database className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-medium text-amber-400">Generated Query</span>
            </div>
            <code className="text-xs text-emerald-400 font-mono block overflow-x-auto">
              {message.query}
            </code>
          </div>
        )}

        {/* Results table */}
        {message.results && message.results.length > 0 && (
          <ResultsTable results={message.results} />
        )}
      </div>
    </div>
  );
};

// --- Suggested Queries ---

const SUGGESTED_QUERIES = [
  'Show me active real estate agents in Miami, FL',
  'How many professionals are in California?',
  'Find cannabis license holders in New York',
  'List vehicle transporters in Texas',
  'Show me brokers in Orlando with active licenses',
  'Count professionals by state',
];

// --- Main Component ---

const ChatPage: React.FC = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('progeodata_token') || '';
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json() as { sessions: ChatSession[] };
        setSessions(data.sessions || []);
      }
    } catch {
      // Silent fail for session loading
    }
  }, [getAuthHeaders]);

  const startNewChat = useCallback(() => {
    setCurrentSessionId(null);
    setMessages([]);
    setError(null);
    inputRef.current?.focus();
  }, []);

  const loadSession = useCallback(async (sessionId: string) => {
    try {
      setError(null);
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json() as { session: ChatSession; messages: any[] };
        setCurrentSessionId(sessionId);
        setMessages(
          (data.messages || []).map((m: any) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            query: m.query_used,
            results: null, // Historical results not stored
            resultsCount: m.results_count,
          }))
        );
      }
    } catch {
      setError('Failed to load chat session.');
    }
  }, [getAuthHeaders]);

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        startNewChat();
      }
    } catch {
      // Silent fail
    }
  }, [getAuthHeaders, currentSessionId, startNewChat]);

  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    setInput('');
    setError(null);

    // Add user message optimistically
    const userMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          message: text,
          sessionId: currentSessionId,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error((errData as any).error || `Chat request failed (${res.status})`);
      }

      const data = await res.json() as {
        sessionId: string;
        message: ChatMessage;
      };

      // Update session ID if new
      if (!currentSessionId && data.sessionId) {
        setCurrentSessionId(data.sessionId);
        // Refresh sessions list
        loadSessions();
      }

      // Add assistant message
      setMessages((prev) => [...prev, data.message]);
    } catch (e: any) {
      setError(e.message || 'Failed to send message');
      // Remove optimistic user message on error
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, currentSessionId, getAuthHeaders, loadSessions]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <Navbar />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? 'w-72' : 'w-0'
          } bg-slate-950 border-r border-slate-800 transition-all duration-300 overflow-hidden flex flex-col`}
        >
          <div className="p-4 border-b border-slate-800">
            <button
              onClick={startNewChat}
              className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Chat
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.length === 0 ? (
              <div className="text-center text-slate-600 text-sm py-8 px-4">
                No chat history yet. Start a new conversation.
              </div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                    currentSessionId === session.id
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                  }`}
                  onClick={() => loadSession(session.id)}
                >
                  <MessageSquare className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm truncate flex-1">{session.title}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Toggle sidebar button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800 hover:bg-slate-700 text-slate-400 p-1.5 rounded-r-lg border border-l-0 border-slate-700 transition-colors"
          style={{ left: sidebarOpen ? '288px' : '0px', transition: 'left 0.3s' }}
        >
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <div className="max-w-3xl mx-auto">
              {messages.length === 0 ? (
                // Empty state with suggestions
                <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-sky-500/10 border border-sky-500/20 mb-4">
                      <Database className="w-8 h-8 text-sky-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">x402 Chat</h2>
                    <p className="text-slate-400 max-w-md">
                      Ask questions about professional license data in natural language.
                      I will translate your questions into database queries.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg w-full">
                    {SUGGESTED_QUERIES.map((query, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(query)}
                        className="text-left text-sm text-slate-300 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/50 hover:border-slate-600 rounded-xl px-4 py-3 transition-all"
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} />
                  ))}

                  {isLoading && (
                    <div className="flex justify-start mb-4">
                      <div className="bg-slate-800/80 rounded-2xl rounded-bl-md border border-slate-700/50 px-4 py-3">
                        <div className="flex items-center gap-2 text-slate-400">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Analyzing your question...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="px-4 pb-2">
              <div className="max-w-3xl mx-auto flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg px-4 py-2.5 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400/60 hover:text-red-400"
                >
                  &times;
                </button>
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-slate-800 bg-slate-900/80 backdrop-blur-sm px-4 py-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-end gap-3 bg-slate-800/60 border border-slate-700/50 rounded-2xl px-4 py-3 focus-within:border-sky-500/50 transition-colors">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about professional license data..."
                  rows={1}
                  className="flex-1 bg-transparent text-white placeholder-slate-500 resize-none outline-none text-sm max-h-32 overflow-y-auto"
                  style={{ minHeight: '24px' }}
                  disabled={isLoading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isLoading}
                  className={`flex-shrink-0 p-2 rounded-xl transition-colors ${
                    input.trim() && !isLoading
                      ? 'bg-sky-600 hover:bg-sky-500 text-white'
                      : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-2 text-center">
                Only SELECT queries are allowed. Results limited to 50 rows. Data may not be current.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ChatPage;
